import { ReactNode, useMemo } from 'react';

import { OrganizationApiProvider } from '@gasbi/shared/web/Organization';
import { useDispatch } from 'react-redux';

import { employeesApi } from '@/features/employees/employeesApi';
import {
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesByRoleQuery,
} from '@/features/employees/employeesApi';
import {
  mainOrganizationApi,
  useGetMainOrganizationRolesInfiniteQuery,
} from '@/features/mainOrganization/mainOrganizationApi';
import { useGetOrganizationByIdQuery } from '@/features/organization/organizationApi';
import {
  organizationsApi,
  useGetCachedOrganizationByIdSelector,
  useGetOrganizationRolesInfiniteQuery,
  useGetOrganizationsInfiniteQuery,
} from '@/features/organizations/organizationsApi';
import { permissionsApi } from '@/features/permissions/permissionsApi';
import {
  useAssignRoleMutation,
  useGetCachedRoleByIdSelector,
  useGetRoleByIdQuery,
  useGetSystemRolesQuery,
  useSaveRoleMutation,
  useSuspendRoleMutation,
} from '@/features/roles/hooks';
import {
  useGetRoleAvailablePermissionsByIdQuery,
  useGetRolePermissionsByIdQuery,
} from '@/features/roles/rolesApi';
import { RootRoleId } from '@/features/roles/types';
import { getRoleLabel, getRoleType, isRootRole } from '@/features/roles/utils';

interface Props {
  children: ReactNode;
}

export const OrganizationApiContextWrapper = ({ children }: Props) => {
  const dispatch = useDispatch();

  const value = useMemo(
    () => ({
      organizationsApi: {
        ...organizationsApi,
        useGetOrganizationByIdQuery,
        useGetOrganizationRolesInfiniteQuery,
        useGetCachedOrganizationByIdSelector,
        useGetOrganizationsInfiniteQuery,
      },
      mainOrganizationApi: {
        ...mainOrganizationApi,
        useGetMainOrganizationByIdQuery: useGetOrganizationByIdQuery,

        useGetMainOrganizationRolesInfiniteQuery,
      },
      rolesApi: {
        useGetSystemRolesQuery,
        useGetRoleByIdQuery,
        useGetCachedRoleByIdSelector,
        useAssignRoleMutation,
        useSuspendRoleMutation,
        useSaveRoleMutation,
        useGetRolePermissionsByIdQuery,
        useGetRoleAvailablePermissionsByIdQuery,
      },
      employeesApi: {
        // ...employeesApi,

        useGetEmployeesByRoleInfiniteQuery,
        useGetEmployeesByRoleQuery,
        useGetEmployeesAccessibleByOrganizationQuery,
        useGetEmployeesInfiniteQuery: () => {},
        useGetDepartmentsIInfiniteQuery: () => {},
        useGetEmployeePositionsIInfiniteQuery: () => {},
      },
      subdivisionsApi: {
        // ...subdivisionsApi,
        useGetSubdivisionByIdQuery: () => ({
          data: [],
          isLoading: false,
        }),

        useGetSubdivisionsInfiniteQuery: () => {},
      },
      locationsApi: {
        useGetSettlementsQuery: () => {},
        useGetStreetsQuery: () => {},
      },
      common: {
        RootRoleId,
        isRootRole,
        getRoleLabel,
        getRoleType,
      },
      utils: {
        invalidateOrgRolesTags: () =>
          dispatch(organizationsApi.util.invalidateTags(['Roles'])),
        invalidateMainOrgRolesTags: () => {},
        invalidateEmployeesByRoleTags: () =>
          dispatch(employeesApi.util.invalidateTags(['EmployeesByRole'])),
        invalidateUserPermissionsTags: () =>
          dispatch(permissionsApi.util.invalidateTags(['UserPermissions'])),
      },
      getOrganizationURL: () => {},
      getMainOrganizationURL: () => {},
      validateCodeFn: () => {},
    }),

    [dispatch],
  );

  return (
    // @ts-ignore
    <OrganizationApiProvider value={value}>{children}</OrganizationApiProvider>
  );
};
